import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Box, Button, Chip, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BuildIcon from '@material-ui/icons/Build';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FindInPageIcon from '@material-ui/icons/FindInPage';
import TimelineIcon from '@material-ui/icons/Timeline';
import type { FetchApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type CapabilitySummaryItem = {
  id: string;
  title: string;
  description: string;
  route: string;
  status: string;
  entityCount: number;
  liveEntityCount: number;
  evidence: string;
};

type CapabilitySummaryGroup = {
  group: string;
  items: CapabilitySummaryItem[];
};

type CapabilityHealthItem = {
  id: string;
  title: string;
  severity: 'blocker' | 'warning' | 'info';
  owner: string;
  capabilityId: string;
  evidence: string;
  action: string;
  route: string;
  requiredAnnotation?: string;
};

type CapabilityHealthCenter = {
  state: 'healthy' | 'attention-required' | 'blocked';
  blockerCount: number;
  warningCount: number;
  infoCount: number;
  items: CapabilityHealthItem[];
};

type IntegrationGateSummary = {
  id: string;
  title: string;
  status: 'active' | 'needs-product-annotation' | 'provider-not-installed';
  requiredAnnotation: string;
  secretReference: string;
  candidateProductCount: number;
  annotatedProductCount: number;
  evidence: string;
};

type CostOwnershipSummary = {
  state:
    | 'finance-grade-ready'
    | 'mapped-with-shared-fallback'
    | 'blocked-missing-config';
  requiredTag: 'c4ci-product-id';
  allocationMethod:
    'azure-resource-ownership-map-then-live-kubernetes-request-share';
  evidence: string;
  action: string;
  sharedFallbackResourceGroups: string[];
  productOwnedResourceGroups: string[];
};

type ProductReadinessDimension = {
  id: string;
  title: string;
  state: 'ready' | 'attention-required' | 'gated' | 'blocker';
  evidence: string;
  action: string;
  route: string;
};

type ProductReadinessItem = {
  productId: string;
  title: string;
  owner: string;
  state: 'ready' | 'attention-required' | 'blocked';
  score: number;
  readyCount: number;
  totalCount: number;
  dimensions: ProductReadinessDimension[];
};

type ProductReadinessSummary = {
  productCount: number;
  readyProductCount: number;
  attentionRequiredProductCount: number;
  blockedProductCount: number;
  products: ProductReadinessItem[];
};

type FactoryGapItem = {
  id: string;
  title: string;
  area: string;
  severity: 'blocker' | 'warning' | 'info';
  source: 'factory-health' | 'product-readiness';
  evidence: string;
  action: string;
  route: string;
  capabilityId?: string;
  productId?: string;
  requiredAnnotation?: string;
};

type FactoryGapBacklog = {
  openCount: number;
  blockerCount: number;
  warningCount: number;
  infoCount: number;
  byArea: Array<{ area: string; count: number }>;
  items: FactoryGapItem[];
};

type CapabilitySummaryResponse = {
  generatedAt: string;
  components: {
    total: number;
    productCount: number;
    githubBackedCount: number;
    techDocsCount: number;
    kubernetesCount: number;
    lighthouseLiveCount: number;
  };
  techInsights: {
    factRetrieverIds: string[];
    checkIds: string[];
  };
  healthCenter: CapabilityHealthCenter;
  integrationGates: IntegrationGateSummary[];
  costOwnership: CostOwnershipSummary;
  productReadiness: ProductReadinessSummary;
  gapBacklog: FactoryGapBacklog;
  capabilities: CapabilitySummaryGroup[];
};

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(0,150,136,0.14), rgba(251,140,0,0.14))',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  card: {
    height: '100%',
  },
  list: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  healthGrid: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  item: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  healthItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  itemTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  metaLine: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  linkRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
}));

function capabilityChipColor(
  status: string,
): 'default' | 'primary' | 'secondary' {
  if (status === 'Live' || status === 'Product fit') {
    return 'primary';
  }

  if (status === 'Not product fit') {
    return 'default';
  }

  return 'secondary';
}

function severityChipColor(
  severity: CapabilityHealthItem['severity'],
): 'default' | 'primary' | 'secondary' {
  if (severity === 'info') {
    return 'primary';
  }

  if (severity === 'blocker') {
    return 'secondary';
  }

  return 'default';
}

function readinessChipColor(
  state: ProductReadinessDimension['state'] | ProductReadinessItem['state'],
): 'default' | 'primary' | 'secondary' {
  if (state === 'ready') {
    return 'primary';
  }
  if (state === 'blocker' || state === 'blocked') {
    return 'secondary';
  }
  return 'default';
}

async function fetchCapabilitySummary(
  fetchApi: FetchApi,
  baseUrl: string,
): Promise<CapabilitySummaryResponse> {
  const response = await fetchApi.fetch(`${baseUrl}/v1/capabilities`);
  if (!response.ok) {
    throw new Error(
      `C4CI capability summary backend returned ${response.status}`,
    );
  }

  return (await response.json()) as CapabilitySummaryResponse;
}

export const FactoryBackstageCapabilitiesPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [summary, setSummary] = useState<CapabilitySummaryResponse>();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('c4ci-factory-runtime');
        const nextSummary = await fetchCapabilitySummary(fetchApi, baseUrl);
        if (!active) {
          return;
        }

        setSummary(nextSummary);
        setState('ready');
        setError(undefined);
      } catch (nextError) {
        if (!active) {
          return;
        }

        setState('error');
        setError(
          nextError instanceof Error ? nextError : new Error(String(nextError)),
        );
      }
    })();

    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  const milestoneCards = summary
    ? [
        {
          icon: <AccountTreeIcon color="primary" />,
          title: 'Discovery',
          text: `${summary.components.productCount} product components are visible through catalog, product visualizer, graph, docs, and API surfaces.`,
        },
        {
          icon: <BuildIcon color="primary" />,
          title: 'Delivery',
          text: `${summary.components.githubBackedCount} product components expose GitHub-backed delivery signals, while provider-specific routes stay gated until real annotations exist.`,
        },
        {
          icon: <AssessmentIcon color="primary" />,
          title: 'Quality',
          text: `${summary.techInsights.checkIds.length} Tech Insights checks and ${summary.techInsights.factRetrieverIds.length} fact retrievers are configured for live scorecards.`,
        },
        {
          icon: <ErrorOutlineIcon color="primary" />,
          title: 'Operations',
          text: `${summary.components.kubernetesCount} product components expose runtime workload mappings and ${summary.components.lighthouseLiveCount} expose live Lighthouse gates.`,
        },
      ]
    : [];

  return (
    <Page themeId="tool">
      <Header
        title="Backstage Capabilities"
        subtitle="Official Backstage plugin coverage for the C4CI Product Factory Portal"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="Official Backstage surface" />
            <Chip label="C4CI product-aware wiring" variant="outlined" />
            <Chip label="External integrations gated" variant="outlined" />
          </div>
          <Typography variant="h4" component="h2" gutterBottom>
            One place to see what the factory can already do, where live product
            data is connected, and which external adapters still need secrets or
            product annotations.
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The portal should expose useful Backstage capability without turning
            missing credentials into red boxes. Pages below are installed and
            linked when safe; entity-specific integrations appear when the
            product catalog declares the required annotation.
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              The official Backstage repository includes product capabilities
              and many provider-specific modules. Factory uses the capabilities
              that map to C4CI product work and gates provider modules until the
              real service, annotations, and secrets exist.
            </Typography>
          </Box>
          {summary ? (
            <Box mt={2} className={classes.chipRow}>
              <Chip
                color="primary"
                label={`${summary.components.productCount} product components`}
              />
              <Chip
                label={`${summary.techInsights.checkIds.length} Tech Insights checks`}
                variant="outlined"
              />
              <Chip
                label={`${summary.components.githubBackedCount} CI/CD-backed repos`}
                variant="outlined"
              />
            </Box>
          ) : null}
          <div className={classes.linkRow}>
            <Button
              color="primary"
              component={RouterLink}
              startIcon={<AccountTreeIcon />}
              to="/product-visualizer"
              variant="contained"
            >
              Open Product Visualizer
            </Button>
            <Button
              color="primary"
              component={RouterLink}
              startIcon={<AccountTreeIcon />}
              to="/catalog-graph"
              variant="outlined"
            >
              Open Catalog Graph
            </Button>
            <Button
              color="primary"
              component={RouterLink}
              startIcon={<TimelineIcon />}
              to="/tech-radar"
              variant="outlined"
            >
              Open Tech Radar
            </Button>
            <Button
              color="primary"
              component={RouterLink}
              startIcon={<FindInPageIcon />}
              to="/catalog-unprocessed-entities"
              variant="outlined"
            >
              Check Catalog Ingestion
            </Button>
          </div>
        </Box>

        <ContentHeader title="Capability Map" />
        {state === 'loading' ? <Progress /> : null}
        {state === 'error' && error ? (
          <ResponseErrorPanel error={error} />
        ) : null}
        {state === 'ready' && summary ? (
          <Grid container spacing={3}>
            {milestoneCards.map(milestone => (
              <Grid item xs={12} md={3} key={milestone.title}>
                <InfoCard className={classes.card} title={milestone.title}>
                  <Box mb={2}>{milestone.icon}</Box>
                  <Typography variant="body2" color="textSecondary">
                    {milestone.text}
                  </Typography>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        ) : null}

        {state === 'ready' && summary ? (
          <Box mt={4}>
            <ContentHeader title="Factory Health Center" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InfoCard
                  className={classes.card}
                  title="Attention Summary"
                >
                  <div className={classes.healthGrid}>
                    <Box className={classes.item}>
                      <Typography variant="overline" color="textSecondary">
                        Runtime state
                      </Typography>
                      <Typography variant="h5">
                        {summary.healthCenter.state}
                      </Typography>
                    </Box>
                    <div className={classes.chipRow}>
                      <Chip
                        color="secondary"
                        label={`${summary.healthCenter.blockerCount} blockers`}
                      />
                      <Chip
                        label={`${summary.healthCenter.warningCount} warnings`}
                        variant="outlined"
                      />
                      <Chip
                        color="primary"
                        label={`${summary.healthCenter.infoCount} info`}
                        variant="outlined"
                      />
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      This is the operator cockpit for gaps that affect the
                      Factory as a live development system: login, cost
                      ownership, integration activation, docs, runtime mappings,
                      and telemetry coverage.
                    </Typography>
                  </div>
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <InfoCard className={classes.card} title="Action Queue">
                  <div className={classes.list}>
                    {summary.healthCenter.items.length > 0 ? (
                      summary.healthCenter.items.map(item => (
                        <Box className={classes.healthItem} key={item.id}>
                          <div className={classes.itemTitle}>
                            <Typography variant="subtitle2">
                              {item.title}
                            </Typography>
                            <Chip
                              color={severityChipColor(item.severity)}
                              label={item.severity}
                              size="small"
                              variant="outlined"
                            />
                          </div>
                          <Typography variant="body2" color="textSecondary">
                            {item.evidence}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="body2">
                              {item.action}
                            </Typography>
                          </Box>
                          <div className={classes.metaLine}>
                            <Chip
                              label={`owner: ${item.owner}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`capability: ${item.capabilityId}`}
                              size="small"
                              variant="outlined"
                            />
                            {item.requiredAnnotation ? (
                              <Chip
                                label={item.requiredAnnotation}
                                size="small"
                                variant="outlined"
                              />
                            ) : null}
                            <Link to={item.route}>Open surface</Link>
                          </div>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No Factory health actions are currently open.
                      </Typography>
                    )}
                  </div>
                </InfoCard>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        {state === 'ready' && summary ? (
          <Box mt={4}>
            <ContentHeader title="Factory Gap Backlog" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InfoCard className={classes.card} title="Open Work">
                  <div className={classes.healthGrid}>
                    <Box className={classes.item}>
                      <Typography variant="overline" color="textSecondary">
                        Catalog and runtime derived
                      </Typography>
                      <Typography variant="h5">
                        {summary.gapBacklog.openCount} open gaps
                      </Typography>
                    </Box>
                    <div className={classes.chipRow}>
                      <Chip
                        color="secondary"
                        label={`${summary.gapBacklog.blockerCount} blockers`}
                      />
                      <Chip
                        label={`${summary.gapBacklog.warningCount} warnings`}
                        variant="outlined"
                      />
                      <Chip
                        color="primary"
                        label={`${summary.gapBacklog.infoCount} info`}
                        variant="outlined"
                      />
                    </div>
                    <div className={classes.metaLine}>
                      {summary.gapBacklog.byArea.map(area => (
                        <Chip
                          key={area.area}
                          label={`${area.area}: ${area.count}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      This backlog is generated from Factory health checks and
                      product readiness evidence. It is the place to see what is
                      still missing without relying on hand-written status notes.
                    </Typography>
                  </div>
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <InfoCard className={classes.card} title="Highest Priority Gaps">
                  <div className={classes.list}>
                    {summary.gapBacklog.items.slice(0, 12).map(item => (
                      <Box className={classes.healthItem} key={item.id}>
                        <div className={classes.itemTitle}>
                          <Typography variant="subtitle2">
                            {item.title}
                          </Typography>
                          <Chip
                            color={severityChipColor(item.severity)}
                            label={`${item.severity} · ${item.area}`}
                            size="small"
                            variant="outlined"
                          />
                        </div>
                        <Typography variant="body2" color="textSecondary">
                          {item.evidence}
                        </Typography>
                        <Box mt={1}>
                          <Typography variant="body2">{item.action}</Typography>
                        </Box>
                        <div className={classes.metaLine}>
                          <Chip
                            label={item.source}
                            size="small"
                            variant="outlined"
                          />
                          {item.productId ? (
                            <Chip
                              label={`product: ${item.productId}`}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                          {item.capabilityId ? (
                            <Chip
                              label={`capability: ${item.capabilityId}`}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                          {item.requiredAnnotation ? (
                            <Chip
                              label={item.requiredAnnotation}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                          <Link to={item.route}>Open evidence</Link>
                        </div>
                      </Box>
                    ))}
                    {summary.gapBacklog.items.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No generated Factory gaps are currently open.
                      </Typography>
                    ) : null}
                  </div>
                </InfoCard>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        {state === 'ready' && summary ? (
          <Box mt={4}>
            <ContentHeader title="Product Readiness Control Center" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InfoCard className={classes.card} title="Readiness Summary">
                  <div className={classes.healthGrid}>
                    <Box className={classes.item}>
                      <Typography variant="overline" color="textSecondary">
                        Products tracked
                      </Typography>
                      <Typography variant="h5">
                        {summary.productReadiness.productCount}
                      </Typography>
                    </Box>
                    <div className={classes.chipRow}>
                      <Chip
                        color="primary"
                        label={`${summary.productReadiness.readyProductCount} ready`}
                      />
                      <Chip
                        label={`${summary.productReadiness.attentionRequiredProductCount} attention required`}
                        variant="outlined"
                      />
                      <Chip
                        color="secondary"
                        label={`${summary.productReadiness.blockedProductCount} blocked`}
                        variant="outlined"
                      />
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      This control center rolls up product-owned readiness
                      dimensions from catalog truth: ownership, source, docs,
                      runtime mapping, telemetry, Lighthouse, and cost
                      ownership. It is intentionally not a hand-written status
                      page.
                    </Typography>
                  </div>
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <InfoCard className={classes.card} title="Product Scorecards">
                  <div className={classes.list}>
                    {summary.productReadiness.products.map(product => (
                      <Box className={classes.healthItem} key={product.productId}>
                        <div className={classes.itemTitle}>
                          <Typography variant="subtitle2">
                            {product.title}
                          </Typography>
                          <Chip
                            color={readinessChipColor(product.state)}
                            label={`${product.score}% ${product.state}`}
                            size="small"
                            variant="outlined"
                          />
                        </div>
                        <Typography variant="body2" color="textSecondary">
                          {product.readyCount}/{product.totalCount} readiness
                          dimensions are backed by live catalog/runtime
                          evidence. Owner: {product.owner}
                        </Typography>
                        <div className={classes.metaLine}>
                          {product.dimensions.map(dimension => (
                            <Chip
                              color={readinessChipColor(dimension.state)}
                              key={`${product.productId}-${dimension.id}`}
                              label={`${dimension.title}: ${dimension.state}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </div>
                        <Box mt={1}>
                          <Link to={`/catalog/default/component/${product.productId}`}>
                            Open product
                          </Link>
                        </Box>
                      </Box>
                    ))}
                  </div>
                </InfoCard>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        {state === 'ready' && summary ? (
          <Box mt={4}>
            <ContentHeader title="Integration Activation And Cost Ownership" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <InfoCard className={classes.card} title="Integration Gates">
                  <div className={classes.list}>
                    {summary.integrationGates.map(gate => (
                      <Box className={classes.healthItem} key={gate.id}>
                        <div className={classes.itemTitle}>
                          <Typography variant="subtitle2">
                            {gate.title}
                          </Typography>
                          <Chip
                            color={
                              gate.status === 'active' ? 'primary' : 'default'
                            }
                            label={gate.status}
                            size="small"
                            variant="outlined"
                          />
                        </div>
                        <Typography variant="body2" color="textSecondary">
                          {gate.evidence}
                        </Typography>
                        <div className={classes.metaLine}>
                          <Chip
                            label={`${gate.annotatedProductCount}/${gate.candidateProductCount} products`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={gate.requiredAnnotation}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={gate.secretReference}
                            size="small"
                            variant="outlined"
                          />
                        </div>
                      </Box>
                    ))}
                  </div>
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={5}>
                <InfoCard
                  className={classes.card}
                  title="Finance-Grade Cost Ownership"
                >
                  <div className={classes.healthGrid}>
                    <Chip
                      color={
                        summary.costOwnership.state === 'finance-grade-ready'
                          ? 'primary'
                          : 'secondary'
                      }
                      label={summary.costOwnership.state}
                      variant="outlined"
                    />
                    <Typography variant="body2" color="textSecondary">
                      {summary.costOwnership.evidence}
                    </Typography>
                    <Typography variant="body2">
                      {summary.costOwnership.action}
                    </Typography>
                    <div className={classes.metaLine}>
                      <Chip
                        label={`required tag: ${summary.costOwnership.requiredTag}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={summary.costOwnership.allocationMethod}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${summary.costOwnership.sharedFallbackResourceGroups.length} shared fallback groups`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${summary.costOwnership.productOwnedResourceGroups.length} product-owned groups`}
                        size="small"
                        variant="outlined"
                      />
                    </div>
                    <Button
                      color="primary"
                      component={RouterLink}
                      to="/project-insights"
                      variant="outlined"
                    >
                      Open Project Insights
                    </Button>
                  </div>
                </InfoCard>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        <Box mt={4}>
          <ContentHeader title="Installed Plugin Inventory" />
          {state === 'ready' && summary ? (
            <Grid container spacing={3}>
              {summary.capabilities.map(group => (
                <Grid item xs={12} md={6} key={group.group}>
                  <InfoCard className={classes.card} title={group.group}>
                    <div className={classes.list}>
                      {group.items.map(item => {
                        const linkLabel =
                          item.status === 'Live' ||
                          item.status === 'Entity gated'
                            ? 'Open'
                            : 'Review status';

                        return (
                          <div className={classes.item} key={item.id}>
                            <div className={classes.itemTitle}>
                              <Typography variant="subtitle2">
                                {item.title}
                              </Typography>
                              <Chip
                                color={capabilityChipColor(item.status)}
                                label={item.status}
                                size="small"
                                variant="outlined"
                              />
                            </div>
                            <Typography variant="body2" color="textSecondary">
                              {item.description}
                            </Typography>
                            <Box mt={1}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {item.evidence}
                              </Typography>
                            </Box>
                            <Box mt={1}>
                              <Link to={item.route}>{linkLabel}</Link>
                            </Box>
                          </div>
                        );
                      })}
                    </div>
                  </InfoCard>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </Box>
      </Content>
    </Page>
  );
};
